// NotificationPanel (DESIGN §3.1.9): Popover 400 × max. 640 an der Glocke (Desktop), mobil die
// Seite /notifications (H2 nutzt `NotificationList`). Gruppen „Today“/„Earlier“, Filter
// All · Mentions · Unread, Ungelesen-Punkt, „Mark all as read“.
import { At, Bell, BellSimple, Gear, WarningCircle } from '@phosphor-icons/react';
import { Popover, RadioGroup } from 'radix-ui';
import { type ReactNode, useMemo, useState } from 'react';
import { Avatar } from '../kern/Avatar';
import { cn } from '../kern/cn';
import { daysFromToday, formatDate, formatRelative } from '../kern/format';
import { RouterLink } from '../kern/Link';
import { ShellIconButton, useIsDesktop } from './parts';

export interface NotificationItem {
  id: string;
  title: string;
  body?: string;
  /** ISO-Zeitpunkt. */
  createdAt: string;
  read: boolean;
  href?: string;
  /** Ziel liegt auf einer anderen Plattform (volle Navigation). */
  reload?: boolean;
  /** Erwähnungen und DMs zählen für den Filter „Mentions“. */
  mention?: boolean;
  actor?: { id: string; name: string };
  icon?: ReactNode;
}

export type NotificationFilter = 'all' | 'mentions' | 'unread';

export interface NotificationListProps {
  items: readonly NotificationItem[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onOpen?: (item: NotificationItem) => void;
  onMarkAllRead?: () => void;
  settingsHref?: string;
  /** Einstellungen liegen auf einer anderen Plattform (volle Navigation). */
  settingsReload?: boolean;
  /** Kopf mit Titel (im Popover ja, auf der Seite H2 nein). */
  showHeader?: boolean;
  className?: string;
}

const FILTERS: { value: NotificationFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mentions', label: 'Mentions' },
  { value: 'unread', label: 'Unread' },
];

function Row({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen?: (item: NotificationItem) => void;
}) {
  const titleId = `notification-${item.id}`;
  const time =
    daysFromToday(item.createdAt) === 0
      ? formatDate(item.createdAt, 'time')
      : formatRelative(item.createdAt);
  const body = (
    <>
      {item.actor ? (
        <Avatar name={item.actor.name} id={item.actor.id} size={32} surface="overlay" decorative />
      ) : (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-icon-circle text-fg-secondary">
          {item.icon ??
            (item.mention ? <At size={16} aria-hidden /> : <BellSimple size={16} aria-hidden />)}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          id={titleId}
          className={cn('line-clamp-2 text-label', item.read ? 'text-fg-secondary' : 'text-fg')}
        >
          {item.title}
        </span>
        {item.body ? (
          <span className="truncate text-small text-fg-tertiary">{item.body}</span>
        ) : null}
        <span className="text-small text-fg-tertiary tabular-nums">{time}</span>
      </span>
      {item.read ? null : (
        <span aria-label="Unread" className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" />
      )}
    </>
  );
  const classes =
    'flex min-h-16 w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-(--bar-dur-fast) hover:bg-card-hover';
  return (
    <article aria-labelledby={titleId}>
      {item.href ? (
        <RouterLink
          href={item.href}
          reload={item.reload}
          className={classes}
          onClick={() => onOpen?.(item)}
        >
          {body}
        </RouterLink>
      ) : (
        <button type="button" className={classes} onClick={() => onOpen?.(item)}>
          {body}
        </button>
      )}
    </article>
  );
}

export function NotificationList({
  items,
  loading,
  error,
  onRetry,
  onOpen,
  onMarkAllRead,
  settingsHref,
  settingsReload,
  showHeader = true,
  className,
}: NotificationListProps) {
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const visible = useMemo(
    () =>
      items.filter((n) =>
        filter === 'unread' ? !n.read : filter === 'mentions' ? n.mention : true,
      ),
    [items, filter],
  );
  const today = visible.filter((n) => daysFromToday(n.createdAt) === 0);
  const earlier = visible.filter((n) => daysFromToday(n.createdAt) !== 0);
  const unread = items.filter((n) => !n.read).length;

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-3 pt-2 pb-2">
        {showHeader ? <h2 className="text-title-detail text-fg">Notifications</h2> : null}
        <div className="flex items-center gap-1">
          <RadioGroup.Root
            aria-label="Filter notifications"
            value={filter}
            onValueChange={(value) => setFilter(value as NotificationFilter)}
            className="inline-flex h-8 items-center rounded-full bg-segment-track p-0.75"
          >
            {FILTERS.map((f) => (
              <RadioGroup.Item
                key={f.value}
                value={f.value}
                className="h-full rounded-full px-2.5 text-label text-fg-secondary transition-colors duration-(--bar-dur-fast) hover:text-fg data-[state=checked]:bg-segment-active data-[state=checked]:text-fg"
              >
                {f.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>
          {settingsHref ? (
            <ShellIconButton
              icon={Gear}
              label="Notification settings"
              href={settingsHref}
              reload={settingsReload}
              size="sm"
              tone="card"
            />
          ) : null}
        </div>
      </div>
      <div
        role="feed"
        aria-busy={loading || undefined}
        aria-label="Notifications"
        className="min-h-0 flex-1 overflow-y-auto px-1 pb-1"
      >
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <div key={i} aria-hidden className="flex gap-3 px-3 py-3">
              <span className="size-8 animate-skeleton rounded-full bg-skeleton" />
              <span className="flex flex-1 flex-col gap-2">
                <span className="h-3 w-4/5 animate-skeleton rounded-full bg-skeleton" />
                <span className="h-3 w-1/3 animate-skeleton rounded-full bg-skeleton" />
              </span>
            </div>
          ))
        ) : error ? (
          // Wie ErrorState (DESIGN §3.11.2): Icon-Kreis 40 `danger-soft`, Titel, Retry.
          <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-danger-soft text-danger-fg">
              <WarningCircle size={20} aria-hidden />
            </span>
            <p className="text-body text-fg">We couldn’t load this.</p>
            <p className="-mt-2 text-label text-fg-secondary">Notifications are unavailable.</p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="h-8 rounded-full bg-pill-card px-4 text-button text-fg hover:bg-pill-card-hover"
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <Bell size={32} aria-hidden className="text-fg-decor" />
            <p className="text-body text-fg">
              {filter === 'all' ? 'You’re all caught up.' : 'Nothing here.'}
            </p>
            <p className="text-label text-fg-secondary">
              New reviews, mentions and approvals will show up here.
            </p>
          </div>
        ) : (
          [
            { label: 'Today', list: today },
            { label: 'Earlier', list: earlier },
          ].map(({ label, list }) =>
            list.length ? (
              <section key={label} aria-label={label}>
                <p className="px-3 pt-2 pb-1 text-chip text-fg-tertiary">{label}</p>
                {list.map((item) => (
                  <Row key={item.id} item={item} onOpen={onOpen} />
                ))}
              </section>
            ) : null,
          )
        )}
      </div>
      {onMarkAllRead && unread > 0 && !loading && !error ? (
        <div className="shrink-0 border-t border-line-subtle px-3 py-2">
          <button
            type="button"
            onClick={onMarkAllRead}
            className="h-8 rounded-full px-2 text-button text-accent-fg hover:underline"
          >
            Mark all as read
          </button>
        </div>
      ) : null}
    </div>
  );
}

export interface NotificationBellProps extends NotificationListProps {
  /** Mobil führt die Glocke auf diese Seite statt ein Popover zu öffnen. */
  pageHref?: string;
  /** Seite liegt auf einer anderen Plattform (volle Navigation). */
  pageReload?: boolean;
}

/** Glocke der Topbar: Punkt bei Ungelesenem, Popover am Desktop, Link mobil. */
export function NotificationBell({
  pageHref = '/notifications',
  pageReload,
  ...list
}: NotificationBellProps) {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const unread = list.items.filter((n) => !n.read).length;
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';
  if (!isDesktop) {
    return (
      <ShellIconButton
        icon={Bell}
        label={label}
        href={pageHref}
        reload={pageReload}
        badge={unread > 0 ? 'dot' : undefined}
        emphasized={unread > 0}
        tooltip={false}
      />
    );
  }
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <ShellIconButton
          icon={Bell}
          label={label}
          badge={unread > 0 ? 'dot' : undefined}
          emphasized={unread > 0}
          tooltip={false}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="z-(--bar-z-popover) flex max-h-[min(640px,calc(100dvh-96px))] w-[min(400px,calc(100vw-16px))] flex-col rounded-xl bg-overlay pt-1 shadow-2 data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out"
        >
          <NotificationList
            {...list}
            onOpen={(item) => {
              list.onOpen?.(item);
              setOpen(false);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
